import eyeFilled from '@iconify/icons-ant-design/eye-filled';
import crossedEyeFilled from '@iconify/icons-ant-design/eye-invisible-filled';
import { Icon } from '@iconify/react';
import React, { useState, forwardRef } from 'react';
import { InputGroup, Tooltip, Whisper } from 'rsuite';
import styled from 'styled-components';
import * as login from './login-form-styles';

export const handleKeyUp = (evt, clickTrigger) => {
  if (evt.key === 'Enter') clickTrigger();
};

// Created a forwardRef version of the input component to fix the ref warning from Whisper after updating rsuite to v5
const ForwardedInput = forwardRef((props, ref) => (
  <login.StyledInput {...props} ref={ref} />
));

export function InputWithTitle(props) {
  // used only if `props.isPasswordInput`
  const [showPassword, setShowPassword] = useState(false);
  const {
    text,
    value,
    focus,
    onInput,
    padding,
    disabled,
    onKeyUp,
    isPasswordInput,
    maxLength,
    whisperMessage,
  } = props;
  const type = isPasswordInput && !showPassword ? 'password' : 'text';
  const icon = showPassword ? eyeFilled : crossedEyeFilled;
  const cypressAttribute = `${text}-textbox`.replace(/\s+/g, '-');

  return (
    <div
      css={`
        padding: ${padding};
        margin-top: 24px;
        width: 300px;
      `}
    >
      <login.StyledText>{text}</login.StyledText>
      <login.StyledInputGroup inside>
        <Whisper
          trigger="focus"
          speaker={<Tooltip>{whisperMessage}</Tooltip>}
          disabled={!whisperMessage}
        >
          <ForwardedInput
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={focus}
            type={type}
            value={value}
            disabled={disabled}
            onInput={onInput}
            onKeyUp={onKeyUp}
            data-cy={cypressAttribute}
            maxLength={maxLength}
          />
        </Whisper>
        {isPasswordInput && (
          <InputGroup.Button onClick={() => setShowPassword(!showPassword)}>
            <Icon icon={icon} />
          </InputGroup.Button>
        )}
      </login.StyledInputGroup>
    </div>
  );
}

export function RentalscapeLogoPlusTitle({ paddingBottom }) {
  return (
    <login.LogoWrapper
      paddingBottom={paddingBottom}
      data-cy="rentalscape-login-logo"
    >
      <login.StyledRentalscapeLogo />
      <login.RentalscapeTitle />
    </login.LogoWrapper>
  );
}

const StepCounterWrapper = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: rgba(16, 25, 56, 0.55);
`;

export function ActivationStepCounter({ phaseNumber, totalPhases, hint }) {
  return (
    <StepCounterWrapper data-cy="activation-step-counter">
      {`Step ${phaseNumber} of ${totalPhases}: ${hint}`}
    </StepCounterWrapper>
  );
}

const EMAIL_TO_RESET = 'Enter email';

export function DefaultEmailInputWithButton(props) {
  const [email, setEmail] = useState('');
  const { onClick, buttonText, loading, inputError, phaseNumber, totalPhases } =
    props;

  return (
    <>
      <RentalscapeLogoPlusTitle />
      <ActivationStepCounter
        phaseNumber={phaseNumber}
        totalPhases={totalPhases}
        hint={EMAIL_TO_RESET}
      />
      <InputWithTitle
        value={email}
        error={inputError}
        onInput={(e) => setEmail(e.target.value)}
        onKeyUp={(evt) => handleKeyUp(evt, () => onClick(email))}
        text="Email"
      />
      <login.StyledFormSubmitButton
        data-cy="email-next-button"
        css="margin-top: 24px;"
        loading={loading}
        disabled={loading}
        onClick={() => onClick(email)}
      >
        {buttonText}
      </login.StyledFormSubmitButton>
    </>
  );
}

const Step3TextWrapper = styled.div`
  width: 300px;
  margin-top: 30px;
  display: flex;
  flex-direction: column;
`;
const Step3Line = styled.div`
  width: 100%;
  margin-top: ${(props) => props.mt || '0px'};
  margin-bottom: ${(props) => props.mb || '0px'};
  font-size: 14px;
  font-weight: ${(props) => (props.bold ? 900 : 'normal')};
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  color: #101938;
`;
const Step3TinyLine = styled(Step3Line)`
  height: 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
`;
const Step3Span = styled.span`
  font-size: 14px;
  font-weight: ${(props) => (props.bold ? 600 : 'normal')};
`;

export function StepThreeInstructionText({ email }) {
  return (
    <Step3TextWrapper>
      <Step3Line>We sent an email with an activation link to: </Step3Line>
      <Step3Line bold mt="8px">
        {email}
      </Step3Line>
      <Step3Line mt="24px" css="line-height: 1.4;">
        To complete the activation process, please click the link on the email
        we just sent to you.
        <Step3Span bold>&nbsp;Make sure to check your spam folder.</Step3Span>
      </Step3Line>
      <Step3TinyLine mt="36px" mb="8px">
        If you haven't received the link, click resend email.
      </Step3TinyLine>
    </Step3TextWrapper>
  );
}
